import type { Season } from '@features/seasonal-theme/types';

import {
  addDays,
  fixedRange,
  blackFriday,
  easterSunday,
  frenchMothersDay,
} from '@features/seasonal-theme/utils/season-dates';

// Shared scales. Every `main` and `dark` reaches at least 4.5:1 against white text (WCAG AA).
const GOLD = {
  lighter: '#FEF3C7',
  light: '#E9C46A',
  main: '#A16207',
  dark: '#854D0E',
  darker: '#4A2C05',
  contrastText: '#FFFFFF',
};
const CHARCOAL = {
  lighter: '#E4E4E7',
  light: '#A1A1AA',
  main: '#27272A',
  dark: '#18181B',
  darker: '#09090B',
  contrastText: '#FFFFFF',
};
const FUCHSIA = {
  lighter: '#FAE8FF',
  light: '#E879F9',
  main: '#A21CAF',
  dark: '#86198F',
  darker: '#4A044E',
  contrastText: '#FFFFFF',
};

/**
 * Ordered by priority: the first season whose range contains today wins,
 * so short events come before the month-long ones they overlap (Halloween > Pink October, Black Friday > Movember).
 */
export const SEASONS: Season[] = [
  {
    id: 'cyber-monday',
    label: 'Cyber Monday',
    range: (year) => {
      const day = addDays(blackFriday(year), 3);
      return { start: day, end: day };
    },
    palette: {
      primary: {
        lighter: '#E0E7FF',
        light: '#818CF8',
        main: '#4F46E5',
        dark: '#4338CA',
        darker: '#312E81',
        contrastText: '#FFFFFF',
      },
      secondary: FUCHSIA,
    },
  },
  {
    id: 'black-friday',
    label: 'Black Friday',
    // Friday to Sunday
    range: (year) => ({ start: blackFriday(year), end: addDays(blackFriday(year), 2) }),
    palette: {
      primary: {
        lighter: '#E4E4E7',
        light: '#71717A',
        main: '#18181B',
        dark: '#09090B',
        darker: '#000000',
        contrastText: '#FFFFFF',
      },
      secondary: GOLD,
    },
  },
  {
    id: 'halloween',
    label: 'Halloween',
    // Pumpkin orange is decorative only: never used for a component color
    decorationColor: '#EA580C',
    range: (year) => fixedRange(year, [10, 24], [10, 31]),
    palette: {
      primary: {
        lighter: '#EDE9FE',
        light: '#A78BFA',
        main: '#6D28D9',
        dark: '#5B21B6',
        darker: '#2E1065',
        contrastText: '#FFFFFF',
      },
      secondary: CHARCOAL,
    },
  },
  {
    id: 'easter',
    label: 'Pâques',
    // The week leading up to Easter Sunday, through Easter Monday
    range: (year) => ({ start: addDays(easterSunday(year), -6), end: addDays(easterSunday(year), 1) }),
    palette: {
      // Lavender and gold, spring pastels darkened to stay readable
      primary: {
        lighter: '#EDE7F6',
        light: '#B39DDB',
        main: '#7E57C2',
        dark: '#5E35B1',
        darker: '#311B92',
        contrastText: '#FFFFFF',
      },
      secondary: GOLD,
    },
  },
  {
    id: 'mothers-day',
    label: 'Fête des mères',
    // The week leading up to the French Mother's Day
    range: (year) => ({ start: addDays(frenchMothersDay(year), -6), end: frenchMothersDay(year) }),
    palette: {
      primary: FUCHSIA,
      secondary: {
        lighter: '#CCFBF1',
        light: '#5EEAD4',
        main: '#0F766E',
        dark: '#115E59',
        darker: '#134E4A',
        contrastText: '#FFFFFF',
      },
    },
  },
  {
    id: 'valentines-day',
    label: 'Saint-Valentin',
    range: (year) => fixedRange(year, [2, 7], [2, 14]),
    palette: {
      primary: {
        lighter: '#FCE7F3',
        light: '#F06BA8',
        main: '#C2185B',
        dark: '#9D174D',
        darker: '#5C0A2E',
        contrastText: '#FFFFFF',
      },
      secondary: {
        lighter: '#FAE8FF',
        light: '#E879F9',
        main: '#86198F',
        dark: '#701A75',
        darker: '#4A044E',
        contrastText: '#FFFFFF',
      },
    },
  },
  {
    id: 'saint-patricks-day',
    label: 'Saint-Patrick',
    range: (year) => fixedRange(year, [3, 14], [3, 17]),
    palette: {
      primary: {
        lighter: '#D1FAE5',
        light: '#34D399',
        main: '#047857',
        dark: '#065F46',
        darker: '#064E3B',
        contrastText: '#FFFFFF',
      },
      secondary: GOLD,
    },
  },
  {
    id: 'christmas',
    label: 'Noël',
    range: (year) => fixedRange(year, [12, 1], [12, 26]),
    palette: {
      // Pine green and gold: the Christmas red stays reserved for errors
      primary: {
        lighter: '#DCF5E3',
        light: '#6BBF8A',
        main: '#166534',
        dark: '#14532D',
        darker: '#052E16',
        contrastText: '#FFFFFF',
      },
      secondary: GOLD,
    },
  },
  {
    id: 'pink-october',
    label: 'Octobre rose',
    range: (year) => fixedRange(year, [10, 1], [10, 31]),
    palette: {
      primary: {
        lighter: '#FCE7F3',
        light: '#F472B6',
        main: '#DB2777',
        dark: '#BE185D',
        darker: '#831843',
        contrastText: '#FFFFFF',
      },
      secondary: {
        lighter: '#E2E8F0',
        light: '#94A3B8',
        main: '#475569',
        dark: '#334155',
        darker: '#1E293B',
        contrastText: '#FFFFFF',
      },
    },
  },
  {
    id: 'movember',
    label: 'Movember',
    range: (year) => fixedRange(year, [11, 1], [11, 30]),
    palette: {
      primary: {
        lighter: '#F3E7DB',
        light: '#C69C72',
        main: '#7C4A21',
        dark: '#5E3617',
        darker: '#3B210D',
        contrastText: '#FFFFFF',
      },
      secondary: {
        lighter: '#E2E8F0',
        light: '#94A3B8',
        main: '#1E293B',
        dark: '#0F172A',
        darker: '#020617',
        contrastText: '#FFFFFF',
      },
    },
  },
];
